import classNames from 'classnames';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import RichText from '@/components/atoms/rich-text/rich-text';
import Spinner from '@/components/atoms/spinner/spinner';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Input from '@/components/molecules/input/input';
import Textarea from '@/components/molecules/textarea/textarea';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import useMutationCreatePaymentRequest from '@/integrations/content/create-payment-request/create-payment-request.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import validateAmount from '@/libraries/validators/validate-amount';
import validateEmail from '@/libraries/validators/validate-email';
import validateNumber from '@/libraries/validators/validate-number';
import AltapayPaymentMethodGroup from './altapay-payment-method-group/altapay-payment-method-group';
import styles from './altapay-payment.module.scss';

export interface AltapayPaymentProps extends ModuleWrapperProps {
  /** The text to show as a headline */
  headline?: Content.Components['AltapayPaymentModuleDto']['headline'];
  /** The text to show when a payment failed */
  paymentFailedMessage: Content.Components['AltapayPaymentModuleDto']['paymentFailedMessage'];
  /** The text to show when a payment succeeded */
  paymentSuccessMessage: Content.Components['AltapayPaymentModuleDto']['paymentSuccessMessage'];
  /** The text to show as a description */
  text?: Content.Components['AltapayPaymentModuleDto']['text'];
}

interface FormValues {
  amount: string;
  bookingNumber: string;
  customerComment: string;
  customerEmail: string;
  customerName: string;
  paymentMethod: string;
}

const AltapayPayment = ({
  headline,
  paymentFailedMessage,
  paymentSuccessMessage,
  text,
  ...rest
}: AltapayPaymentProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useI18n();
  const [status, setStatus] = useState<
    'initial' | 'paymentMethod' | 'succeeded' | 'failed'
  >('initial');
  const {
    clearErrors,
    control,
    formState,
    getValues,
    handleSubmit,
    register,
    setError,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      amount: '',
      bookingNumber: '',
      customerComment: '',
      customerEmail: '',
      customerName: '',
      paymentMethod: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const formValues = watch();
  const {
    data: dataCreatePaymentRequest,
    error: errorCreatePaymentRequest,
    isLoading: isMutatingCreatePaymentRequest,
    isSuccess: isSuccessCreatePaymentRequest,
    mutate: mutateCreatePaymentRequest,
  } = useMutationCreatePaymentRequest();

  useEffect(() => {
    if (searchParams!.get('payment') === 'succeeded') {
      setStatus('succeeded');
    } else if (searchParams!.get('payment') === 'failed') {
      setStatus('failed');
    }
  }, [searchParams]);

  useEffect(() => {
    if (!errorCreatePaymentRequest) {
      clearErrors();
    } else {
      if (!errorCreatePaymentRequest.errors) return;

      Object.keys(errorCreatePaymentRequest.errors).forEach(key => {
        setError(key as keyof FormValues, {
          message: errorCreatePaymentRequest.errors[key][0],
          type: 'custom',
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCreatePaymentRequest]);

  const onSubmit: SubmitHandler<FormValues> = data => {
    mutateCreatePaymentRequest(data);
  };

  const getErrorMessage = (fieldName: keyof FormValues): string | undefined => {
    const error = formState.errors[fieldName];

    if (!error) return;

    return error.message;
  };

  useEffect(() => {
    if (!dataCreatePaymentRequest || !isSuccessCreatePaymentRequest) return;

    router.push(dataCreatePaymentRequest.url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCreatePaymentRequest]);

  const isDisabledChoosePaymentMethod = useMemo(() => {
    return (
      !validateAmount.test(getValues('amount')) ||
      !validateNumber.test(getValues('bookingNumber')) ||
      !validateEmail.test(getValues('customerEmail')) ||
      !getValues('amount') ||
      !getValues('bookingNumber') ||
      !getValues('customerEmail') ||
      !getValues('customerName')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  const isDisabledContinueToPayment = useMemo(() => {
    return !getValues('paymentMethod');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  const renderContent = () => {
    if (status === 'failed') {
      return paymentFailedMessage && <RichText text={paymentFailedMessage} />;
    }

    if (status === 'succeeded') {
      return paymentSuccessMessage && <RichText text={paymentSuccessMessage} />;
    }

    return (
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        {isMutatingCreatePaymentRequest && <Spinner loadingText="" />}

        {status === 'paymentMethod' ? (
          <React.Fragment>
            <AltapayPaymentMethodGroup
              amount={getValues('amount')}
              control={control as any}
              error={getErrorMessage('paymentMethod')}
              id="paymentMethod"
              name="paymentMethod"
              options={[
                {
                  label: t('items.form.dankort'),
                  value: 'Dankort',
                },
                {
                  label: t('items.form.mastercard'),
                  value: 'Mastercard',
                },
                {
                  label: t('items.form.mobilepay'),
                  value: 'Mobilepay',
                },
              ]}
              required
            />

            <div className={styles.buttons}>
              <Button
                onClick={() => setStatus('initial')}
                text={t('items.form.back')}
                variant="secondary"
              />

              <Button
                disabled={isDisabledContinueToPayment}
                onClick={() => {
                  if (isDisabledContinueToPayment) return;

                  handleSubmit(onSubmit);
                }}
                text={t('items.form.continueToPayment')}
                type="submit"
              />
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Input
              {...register('amount', {
                pattern: {
                  message: t('items.form.validationAmount'),
                  value: validateAmount,
                },
                required: t('items.form.validationRequired'),
              })}
              error={getErrorMessage('amount')}
              id="amount"
              label={t('items.form.amount')}
              required
            />

            <Input
              {...register('bookingNumber', {
                pattern: {
                  message: t('items.form.validationNumber'),
                  value: validateNumber,
                },
                required: t('items.form.validationRequired'),
              })}
              error={getErrorMessage('bookingNumber')}
              id="bookingNumber"
              label={t('items.form.bookingNumber')}
              required
            />

            <Input
              {...register('customerName', {
                required: t('items.form.validationRequired'),
              })}
              error={getErrorMessage('customerName')}
              id="customerName"
              label={t('items.form.name')}
              required
            />

            <Input
              {...register('customerEmail', {
                pattern: {
                  message: t('items.form.validationEmail'),
                  value: validateEmail,
                },
                required: t('items.form.validationRequired'),
              })}
              error={getErrorMessage('customerEmail')}
              id="customerEmail"
              label={t('items.form.email')}
              required
            />

            <Textarea
              {...register('customerComment')}
              error={getErrorMessage('customerComment')}
              id="customerComment"
              label={t('items.form.comment')}
            />

            <div className={styles.buttons}>
              <Button
                onClick={() => {
                  if (isDisabledChoosePaymentMethod) return;

                  setStatus('paymentMethod');
                }}
                text={t('items.form.choosePaymentMethod')}
              />
            </div>
          </React.Fragment>
        )}
      </form>
    );
  };

  return (
    <ModuleWrapper {...rest} className={classNames(styles.component)}>
      <Container>
        <Row className={styles.row}>
          <Column
            className={styles.textColumn}
            order={{ laptop: 1 }}
            width={{ tablet: 5 }}
          >
            {headline && (
              <Typography className={styles.headline} tagStyle="displayMedium">
                {headline}
              </Typography>
            )}

            {text && <RichText className={styles.text} text={text} />}
          </Column>

          <Column
            offset={{ laptop: 1 }}
            order={{ laptop: 2 }}
            width={{ laptop: 6 }}
          >
            {renderContent()}
          </Column>
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

AltapayPayment.displayName = 'AltapayPayment';

export default AltapayPayment;
